import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, Skeleton, Alert, Tabs, Tab, Avatar, Card, CardContent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import MainLayout from '../layouts/MainLayout';
import UserForm from '../components/UserForm';
import UserTable from '../components/UserTable';
import useUserStore from '../store/useUserStore';
import useNotificationStore from '../store/useNotificationStore';

const UserManagementPage = () => {
  const { users, loading, error, fetchUsers, addUser, updateUser, removeUser } = useUserStore();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const [editingUser, setEditingUser] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSaveUser = async (userData) => {
    try {
      if (editingUser) {
        await updateUser(editingUser.id, userData);
        showNotification('Pengguna berhasil diperbarui', 'success');
      } else {
        await addUser(userData);
        showNotification('Pengguna berhasil ditambahkan', 'success');
      }
      setIsFormOpen(false);
      setEditingUser(null);
    } catch (err) {
      showNotification(`Gagal menyimpan pengguna: ${err}`, 'error');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsFormOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus pengguna ini?')) {
      try {
        await removeUser(userId);
        showNotification('Pengguna berhasil dihapus', 'success');
      } catch (err) {
        showNotification(`Gagal menghapus pengguna: ${err}`, 'error');
      }
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingUser(null);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Filter users based on active tab
  const filteredUsers = users.filter(user => {
    if (tabValue === 0) return true; // Semua
    if (tabValue === 1) return user.role === 'LAZISNU';
    if (tabValue === 2) return user.role === 'MWC';
    if (tabValue === 3) return user.role === 'RANTING';
    return true;
  });

  return (
    <MainLayout>
      <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        {/* HEADER */}
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, display: 'flex', flexDirection: {xs:'column', md:'row'}, alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: {xs: 2, md: 0} }}>
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', mr: 2, width: 56, height: 56 }}>
                    <PeopleIcon fontSize="large" />
                </Avatar>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color="primary.main">Manajemen Pengguna</Typography>
                    <Typography variant="body2" color="text.secondary">Kelola akun dan hak akses sistem.</Typography>
                </Box>
            </Box>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddUser} sx={{ borderRadius: 2, px: 3 }}>
                Tambah Pengguna
            </Button>
        </Paper>

        {isFormOpen && (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 3 }}>
            <UserForm
              userToEdit={editingUser}
              onSubmit={handleSaveUser}
              onCancel={handleCloseForm}
            />
          </Paper>
        )}

        {error && <Alert severity="error" sx={{ mt: 3, mb: 3 }}>{error}</Alert>}

        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange} 
                    indicatorColor="primary" 
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ px: 2, pt: 1 }}
                >
                    <Tab label="Semua Pengguna" />
                    <Tab label="Lazisnu" />
                    <Tab label="Unit MWC" />
                    <Tab label="Unit Ranting" />
                </Tabs>
            </Box>
            <CardContent>
                {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={300} />
                ) : (
                    <UserTable
                        users={filteredUsers}
                        onEdit={handleEditUser}
                        onDelete={handleDeleteUser}
                    />
                )}
            </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
};

export default UserManagementPage;